/* eslint-disable jsx-a11y/alt-text */
import React, { FC, useState } from 'react';
import { createPortal } from 'react-dom';
import CarouselFullScreenMode from './CarouselFullScreenMode';
import { IconArrowForward, IconZoomIn, IconArrowBack } from '../../images';
import { Box, Typography } from '@mui/material';
import CustomList from '../CustomList/CustomList';
import { SmartImage } from '@cellebrite/saas-ui-common';

export interface GalleryImage {
    src: string;
    label?: string;
    hideLabel?: boolean;
    imageProps?: any;
}

interface ImagesCarouselProps {
    keepFailedImages?: boolean;
    containerStyles?: object;
    items: GalleryImage[];
    size?: number;
}

const INITIAL_POSITION = 0;
const INCREMENT_BY = 1;
const ImagesCarousel: FC<ImagesCarouselProps> = ({ items, containerStyles, size, keepFailedImages }) => {
    const [currentImagePosition, setCurrentImagePosition] = useState<number>(INITIAL_POSITION);
    const [showModal, setShowModal] = useState(false);
    const [failedSources, setFailedSources] = useState([]);
    const [loadedSources, setLoadedSources] = useState([]);

    const onNextItem = () => {
        setCurrentImagePosition(
            currentImagePosition !== items.length - INCREMENT_BY
                ? currentImagePosition + INCREMENT_BY
                : INITIAL_POSITION,
        );
    };

    const onPreviousItem = () => {
        setCurrentImagePosition(
            currentImagePosition !== INITIAL_POSITION
                ? currentImagePosition - INCREMENT_BY
                : items.length - INCREMENT_BY,
        );
    };

    const filteredItems = keepFailedImages ? items : items.filter((item) => !failedSources.includes(item.src));
    const itemsProcessed = loadedSources.length + failedSources.length === items.length;
    const defaultContainerStyles = { position: 'relative', display: itemsProcessed ? null : '-webkit-box' };
    const finalContainerStyles = { ...defaultContainerStyles, ...(containerStyles ? containerStyles : {}) };
    if (size) {
        finalContainerStyles['height'] = size;
    }

    return (
        // @ts-ignore
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: size ? size : '-webkit-fill-available',
                height: size ? `calc(${size}px + 32.5px)` : 'calc(100% + 32.5px)',
                overflow: 'hidden',
            }}
        >
            <Box sx={finalContainerStyles}>
                {filteredItems.map((item, index) => {
                    const hiddenImagesStyles = {
                        styles: { position: index === currentImagePosition || size ? null : 'absolute' },
                    };
                    if (index !== currentImagePosition && size) {
                        hiddenImagesStyles['containerStyles'] = { display: 'none' };
                    }
                    return (
                        <SmartImage
                            key={index}
                            src={item.src}
                            {...{
                                ...{
                                    actionsElement: (
                                        <Box
                                            key={`zoomin${index}`}
                                            sx={{
                                                m: 0.5,
                                                p: 1,
                                                lineHeight: 0,
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                            }}
                                            onClick={(e) => {
                                                setShowModal(true);
                                                e.stopPropagation();
                                            }}
                                            className="zoom-in-icon background-color-bd"
                                        >
                                            <IconZoomIn className="fill-l" />
                                        </Box>
                                    ),
                                },
                                ...{
                                    ...item.imageProps,
                                    ...hiddenImagesStyles,
                                },
                            }}
                            onFail={() => {
                                if (!loadedSources.includes(item.src)) {
                                    setFailedSources((currentFailedSources) => [...currentFailedSources, item.src]);
                                }
                            }}
                            onLoad={() => {
                                if (failedSources.includes(item.src)) {
                                    setFailedSources(failedSources.filter(() => failedSources.includes(item.src)));
                                }
                                setTimeout(() =>
                                    setLoadedSources((currentLoadedSources) => {
                                        if (!currentLoadedSources.includes(item.src)) {
                                            currentLoadedSources.push(item.src);
                                        }
                                        return [...currentLoadedSources];
                                    }),
                                );
                            }}
                        />
                    );
                })}
                {itemsProcessed && filteredItems.length > 1 && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <Box
                            sx={{
                                mr: 1.5,
                                p: 1,
                                borderRadius: '8px',
                                lineHeight: 0,
                                cursor: 'pointer',
                                userSelect: 'none',
                            }}
                            className="background-color-bd-opacity"
                            onClick={onNextItem}
                        >
                            <IconArrowForward className="fill-l" />
                        </Box>
                    </Box>
                )}
                {itemsProcessed && filteredItems.length > 1 && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <Box
                            sx={{
                                ml: 1.5,
                                p: 1,
                                borderRadius: '8px',
                                lineHeight: 0,
                                cursor: 'pointer',
                            }}
                            className="background-color-bd-opacity"
                            onClick={onPreviousItem}
                        >
                            <IconArrowBack className="fill-l" />
                        </Box>
                    </Box>
                )}

                {!filteredItems[currentImagePosition]?.hideLabel && filteredItems[currentImagePosition]?.label && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            m: '8px 15px',
                            pl: 1,
                            pr: 1,
                            borderRadius: '4px',
                            boxShadow:
                                '0px 0px 2px rgba(145, 158, 171, 0.24), 0px 16px 32px -4px rgba(145, 158, 171, 0.24)',
                            userSelect: 'none',
                        }}
                        className="background-color-l"
                    >
                        <Typography>{filteredItems[currentImagePosition].label}</Typography>
                    </Box>
                )}
            </Box>
            {itemsProcessed && filteredItems.length > 1 && (
                <CustomList
                    styles={{ mt: 1, mb: 1, justifyContent: 'center' }}
                    gap={{ width: 1, height: 1 }}
                    list={filteredItems.map((item, index) => (
                        <SmartImage
                            src={item.src}
                            key={index}
                            containerStyles={{
                                width: 35.88,
                                height: 24.5,
                                border: `${currentImagePosition === index ? 2 : 0}px solid`,
                                userSelect: 'none',
                            }}
                            roundedCorners={2}
                            containerClassName={currentImagePosition === index ? 'border-color-b' : ''}
                            onClick={() => setCurrentImagePosition(index)}
                        />
                    ))}
                />
            )}
            {showModal &&
                createPortal(
                    <CarouselFullScreenMode
                        onClose={() => setShowModal(false)}
                        items={filteredItems}
                        currentImagePosition={currentImagePosition}
                        onNextItem={onNextItem}
                        onPreviousItem={onPreviousItem}
                    />,
                    document.getElementById('root'),
                )}
        </Box>
    );
};

export default ImagesCarousel;
